import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Search, Filter, Calendar, FileText, Users, ChevronLeft, ChevronRight } from 'lucide-react'
import { clientService } from '../../services/clientService'

const ClientCases = () => {
  // Client authentication is handled by ProtectedClientRoute wrapper
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState('all')
  const [page, setPage] = useState(1)
  const limit = 10

  // Fetch cases with filters
  const { data: casesData, isLoading, error } = useQuery({
    queryKey: ['clientCases', status, page, limit],
    queryFn: () => clientService.getCases({
      status: status !== 'all' ? status : undefined,
      limit,
      offset: (page - 1) * limit,
    }),
    staleTime: 2 * 60 * 1000,
  })

  // Compute statistics
  const stats = {
    active: casesData?.cases.filter(c => c.status === 'active' || c.status === 'Active').length || 0,
    pending: casesData?.cases.filter(c => c.status === 'pending' || c.status === 'Pending').length || 0,
    closed: casesData?.cases.filter(c => c.status === 'closed' || c.status === 'Closed' || c.status === 'Completed').length || 0,
  }

  const filteredCases = casesData?.cases.filter(c => 
    search === '' || 
    c.case_number.toLowerCase().includes(search.toLowerCase()) ||
    c.title.toLowerCase().includes(search.toLowerCase()) ||
    (c.responsible_lawyer?.first_name + ' ' + c.responsible_lawyer?.last_name).toLowerCase().includes(search.toLowerCase())
  ) || []

  const totalPages = casesData?.total ? Math.ceil(casesData.total / limit) : 1

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    } catch {
      return dateString
    }
  }

  const handlePrevPage = () => {
    if (page > 1) setPage(page - 1)
  }

  const handleNextPage = () => {
    if (page < totalPages) setPage(page + 1)
  }

  return (
    <div className="p-6">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">My Cases</h1>
        <p className="text-gray-600 mt-1">View all your legal cases and their status.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search cases by number, title, or lawyer..."
                  className="pl-10 w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
              <div className="flex items-center gap-3">
                <div className="flex items-center">
                  <Filter className="h-5 w-5 text-gray-400 mr-2" />
                  <select
                    className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    value={status}
                    onChange={(e) => {
                      setStatus(e.target.value)
                      setPage(1)
                    }}
                  >
                    <option value="all">All Status</option>
                    <option value="active">Active</option>
                    <option value="pending">Pending</option>
                    <option value="closed">Closed</option>
                    <option value="completed">Completed</option>
                  </select>
                </div>
              </div>
            </div>

            {isLoading ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
              </div>
            ) : error ? (
              <div className="py-12 text-center text-red-600">
                Failed to load cases. Please try again.
              </div>
            ) : filteredCases.length === 0 ? (
              <div className="py-12 text-center text-gray-500">
                <FileText className="h-12 w-12 mx-auto text-gray-300 mb-4" />
                <p className="text-lg">No cases found.</p>
                <p className="text-sm">Try adjusting your filters or search.</p>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead>
                      <tr className="bg-gray-50">
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Case #</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Title</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Updated</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Next Deadline</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {filteredCases.map((c) => (
                        <tr key={c.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => window.location.href = `/client/cases/${c.id}`}>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <div className="text-sm font-medium text-blue-600">{c.case_number}</div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="text-sm font-medium text-gray-900">{c.title}</div>
                            <div className="text-xs text-gray-500 flex items-center">
                              <Users className="h-3 w-3 mr-1" />
                              Lawyer: {c.responsible_lawyer ? `${c.responsible_lawyer.first_name} ${c.responsible_lawyer.last_name}` : 'Not assigned'}
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`inline-flex px-3 py-1 text-xs font-medium rounded-full ${
                              c.status.toLowerCase() === 'active' || c.status.toLowerCase() === 'in progress'
                                ? 'bg-green-100 text-green-800'
                                : c.status.toLowerCase() === 'pending'
                                ? 'bg-yellow-100 text-yellow-800'
                                : 'bg-gray-100 text-gray-800'
                            }`}>
                              {c.status}
                            </span>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                            <div className="flex items-center">
                              <Calendar className="h-4 w-4 mr-2" />
                              {formatDate(c.opened_at)}
                            </div>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                            {c.upcoming_deadlines && c.upcoming_deadlines.length > 0 ? (
                              <div>
                                {formatDate(c.upcoming_deadlines[0].due_date)}
                                <div className="text-xs text-amber-600">
                                  {c.upcoming_deadlines[0].days_left > 0 ? `${c.upcoming_deadlines[0].days_left} days left` : 'Overdue'}
                                </div>
                              </div>
                            ) : 'N/A'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="mt-6 flex flex-col sm:flex-row justify-between items-center gap-4">
                  <div className="text-sm text-gray-500">
                    Showing <span className="font-medium">{(page - 1) * limit + 1}</span> to{' '}
                    <span className="font-medium">{Math.min(page * limit, casesData?.total || 0)}</span> of{' '}
                    <span className="font-medium">{casesData?.total || 0}</span> cases
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={handlePrevPage}
                      disabled={page === 1}
                      className={`px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium ${page === 1 ? 'text-gray-400 cursor-not-allowed' : 'text-gray-700 hover:bg-gray-50'}`}
                    >
                      <ChevronLeft className="h-4 w-4 inline mr-1" />
                      Previous
                    </button>
                    <button
                      onClick={handleNextPage}
                      disabled={page >= totalPages}
                      className={`px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium ${page >= totalPages ? 'text-gray-400 cursor-not-allowed' : 'text-gray-700 hover:bg-gray-50'}`}
                    >
                      Next
                      <ChevronRight className="h-4 w-4 inline ml-1" />
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        <div className="lg:col-span-1">
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-5">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Case Statistics</h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className="h-3 w-3 rounded-full bg-blue-500 mr-3"></div>
                  <span className="text-sm text-gray-700">Active Cases</span>
                </div>
                <span className="text-lg font-bold">{stats.active}</span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className="h-3 w-3 rounded-full bg-yellow-500 mr-3"></div>
                  <span className="text-sm text-gray-700">Pending</span>
                </div>
                <span className="text-lg font-bold">{stats.pending}</span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className="h-3 w-3 rounded-full bg-gray-400 mr-3"></div>
                  <span className="text-sm text-gray-700">Closed</span>
                </div>
                <span className="text-lg font-bold">{stats.closed}</span>
              </div>
            </div>
            <div className="mt-6 pt-6 border-t border-gray-200">
              <h4 className="text-sm font-medium text-gray-900 mb-3">Need Help?</h4>
              <p className="text-sm text-gray-600 mb-4">
                Contact your assigned lawyer directly or submit a support request.
              </p>
              <button
                onClick={() => window.location.href = '/client/profile?tab=support'}
                className="w-full py-2.5 px-4 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700"
              >
                Request Support
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ClientCases